import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { movies } from "@/mocks/movies";
import { applyQuery } from "@/hooks/useMovieQuery";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieFilterBar from "@/components/feature/MovieFilterBar";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 8;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") || "";
  const genre = searchParams.get("genre") || "全部";
  const region = searchParams.get("region") || "全部";
  const year = searchParams.get("year") || "全部";
  const sort = searchParams.get("sort") || "default";
  const page = Number(searchParams.get("page") || "1");

  const [keyword, setKeyword] = useState(q);

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  useSeo({
    title: q
      ? `“${q}”搜索结果 - 高清影视资源 | 《出轨的妻子》hd`
      : "搜索影视资源 - 高清影视资源 | 《出轨的妻子》hd",
    description:
      "在《出轨的妻子》hd 站内搜索电影名称、类型、导演或主演，快速定位高清影视资源，并支持筛选与排序。",
    keywords: "影视搜索,高清电影搜索,出轨的妻子,影视资源",
  });

  const results = useMemo(
    () => applyQuery(movies, { keyword, genre, region, year, sort }),
    [keyword, genre, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const paged = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateParams = (
    patch: Record<string, string>,
    options: { resetPage?: boolean; replace?: boolean } = { resetPage: true },
  ) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "全部" || (key === "sort" && value === "default")) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    if (options.resetPage) next.delete("page");
    setSearchParams(next, { replace: options.replace ?? false });
  };

  const handleKeywordChange = (value: string) => {
    setKeyword(value);
    updateParams({ q: value.trim() }, { resetPage: true, replace: true });
  };

  const resetAll = () => {
    setKeyword(q);
    updateParams({ genre: "全部", region: "全部", year: "全部", sort: "default" });
  };

  const hotMovies = useMemo(() => [...movies].sort((a, b) => b.score - a.score).slice(0, 6), []);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">搜索影视资源</h1>
        <p className="mt-2 text-sm text-foreground-600">
          支持电影名称、类型、导演与主演的模糊搜索。
        </p>
      </header>

      <div className="mt-5 flex items-center gap-3 rounded-lg border border-background-300 bg-background-50 px-4 py-3 focus-within:border-primary-400 md:px-5">
        <i className="ri-search-line text-xl text-foreground-400" aria-hidden="true"></i>
        <input
          type="text"
          value={keyword}
          onChange={(e) => handleKeywordChange(e.target.value)}
          placeholder="输入电影名称、类型、导演或主演关键词"
          className="w-full border-0 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none md:text-base"
          aria-label="搜索关键词"
        />
        {keyword && (
          <button
            type="button"
            onClick={() => handleKeywordChange("")}
            className="flex h-7 w-7 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 cursor-pointer"
            aria-label="清空关键词"
          >
            <i className="ri-close-line text-lg" aria-hidden="true"></i>
          </button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 font-label text-sm text-foreground-600">
        <span>
          {keyword ? (
            <>
              关键词 <strong className="font-semibold text-primary-600">“{keyword}”</strong> 共找到
            </>
          ) : (
            <>当前展示全部影视资源，共</>
          )}
        </span>
        <strong className="font-semibold text-accent-600">{results.length}</strong>
        <span>个结果</span>
      </div>

      <div className="mt-5">
        <MovieFilterBar
          genre={genre}
          region={region}
          year={year}
          sort={sort}
          onChange={(patch) => updateParams(patch)}
          onReset={resetAll}
        />
      </div>

      {paged.length > 0 ? (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {paged.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          <Pagination
            current={currentPage}
            totalPages={totalPages}
            onChange={(p) => updateParams({ page: String(p) }, { resetPage: false })}
          />
        </>
      ) : (
        <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-search-eye-line text-3xl" aria-hidden="true"></i>
          </span>
          <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-900">无相关影视资源</h2>
          <p className="mt-2 text-sm text-foreground-500">
            没有找到与“{keyword}”相关的电影，换个关键词试试，或者看看下面的热门推荐。
          </p>
          <button
            type="button"
            onClick={() => handleKeywordChange("")}
            className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            清空关键词
          </button>

          <div className="mt-10 border-t border-background-200 pt-8">
            <h3 className="font-heading text-base font-semibold text-foreground-950">热门推荐</h3>
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5">
              {hotMovies.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <div className="h-14 w-10 shrink-0 overflow-hidden rounded bg-background-200">
                    <img
                      src={movie.poster}
                      alt={`${movie.title}海报`}
                      title={`${movie.title}海报`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="truncate font-label text-sm text-foreground-900">{movie.title}</p>
                    <p className="mt-0.5 font-label text-xs text-foreground-500">
                      {movie.year} · {movie.genres[0]}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}